"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.currentOrderingWeek = exports.cutoffForWeek = exports.cutoffPrecedesWeekOpening = exports.DEFAULT_CUTOFF_RULE = exports.weekdayOfParts = exports.isRealWetacaWeek = exports.startOfWetacaWeek = exports.deliveryDateForOrderWeek = exports.madridInstantAt = exports.deliveryDateForWeekday = exports.getMadridDateParts = exports.madridStartOfDay = exports.getWetacaWeekAndYear = exports.nextStartOfWeek = exports.currentEndOfWeek = exports.currentStartOfWeek = exports.WEEK_SCHEDULE = void 0;
const date_fns_1 = require("date-fns");
const date_fns_tz_1 = require("date-fns-tz");
const MADRID = 'Europe/Madrid';
exports.WEEK_SCHEDULE = {
    day: 4, // Thursday (0=Sun...6=Sat)
    hours: 8,
    minutes: 15,
};
/**
 * Returns the Thursday 08:15 Madrid-time that starts the Wetaca week containing `date`.
 * If `date` is exactly at or after the boundary Thursday 08:15, that Thursday is the start.
 * If `date` is before that Thursday 08:15, we step back one week.
 */
const currentStartOfWeek = (date = new Date()) => {
    const inMadrid = (0, date_fns_tz_1.toZonedTime)(date, MADRID);
    const dayOfWeek = inMadrid.getDay(); // 0=Sun...6=Sat
    const thursday = exports.WEEK_SCHEDULE.day; // 4
    // Days since last thursday (or today if thursday)
    const daysSinceThursday = (dayOfWeek - thursday + 7) % 7;
    // Compute candidate thursday in Madrid local time
    const candidateThursday = new Date(inMadrid);
    candidateThursday.setDate(inMadrid.getDate() - daysSinceThursday);
    candidateThursday.setHours(exports.WEEK_SCHEDULE.hours, exports.WEEK_SCHEDULE.minutes, 0, 0);
    // If current date is before the candidate thursday boundary, step back one week
    if (inMadrid < candidateThursday) {
        candidateThursday.setDate(candidateThursday.getDate() - 7);
    }
    // Convert back to UTC
    return (0, date_fns_tz_1.fromZonedTime)(candidateThursday, MADRID);
};
exports.currentStartOfWeek = currentStartOfWeek;
/**
 * Returns the Thursday 08:15 Madrid-time that ends the Wetaca week containing `date`.
 * This is the start of the NEXT Wetaca week.
 */
const currentEndOfWeek = (date = new Date()) => {
    const start = (0, exports.currentStartOfWeek)(date);
    // Step +7 CALENDAR days in Madrid wall-clock, not 168 absolute hours: this
    // keeps the boundary at Thursday 08:15 Madrid even across a DST change (the
    // week that contains the spring/autumn switch is 167h/169h long). Uses the
    // same explicit-Madrid conversion as currentStartOfWeek so the result never
    // depends on the process TZ — `addWeeks` (setDate, runtime-TZ) drifted 1h on
    // DST weeks under any non-Madrid runtime.
    const startInMadrid = (0, date_fns_tz_1.toZonedTime)(start, MADRID);
    startInMadrid.setDate(startInMadrid.getDate() + 7);
    return (0, date_fns_tz_1.fromZonedTime)(startInMadrid, MADRID);
};
exports.currentEndOfWeek = currentEndOfWeek;
/**
 * Returns the Thursday 08:15 Madrid-time that starts the NEXT Wetaca week after the one containing `date`.
 * Equivalent to currentEndOfWeek.
 */
const nextStartOfWeek = (date = new Date()) => {
    return (0, exports.currentEndOfWeek)(date);
};
exports.nextStartOfWeek = nextStartOfWeek;
/**
 * Returns the ISO week/year label for a date's Wetaca week.
 * Label = ISO week/year of the closing Thursday (currentEndOfWeek).
 * This is the "last Thursday contained in the interval" convention.
 */
const getWetacaWeekAndYear = (date = new Date()) => {
    const endThursday = (0, exports.currentEndOfWeek)(date);
    // Use the closing Thursday in Madrid time for ISO labeling
    const endInMadrid = (0, date_fns_tz_1.toZonedTime)(endThursday, MADRID);
    return {
        week: (0, date_fns_1.getISOWeek)(endInMadrid),
        year: (0, date_fns_1.getISOWeekYear)(endInMadrid),
    };
};
exports.getWetacaWeekAndYear = getWetacaWeekAndYear;
/**
 * Returns the UTC instant of Madrid-local midnight (00:00) of `date`'s day.
 * TZ is pinned to Europe/Madrid, never the runtime TZ.
 */
const madridStartOfDay = (date) => {
    const inMadrid = (0, date_fns_tz_1.toZonedTime)(date, MADRID);
    inMadrid.setHours(0, 0, 0, 0);
    return (0, date_fns_tz_1.fromZonedTime)(inMadrid, MADRID);
};
exports.madridStartOfDay = madridStartOfDay;
/**
 * Extracts the Madrid-local calendar day/month/year of `date`.
 * `month` is 1-based (1=January), matching DeliveryOptionException.month.
 */
const getMadridDateParts = (date) => {
    const inMadrid = (0, date_fns_tz_1.toZonedTime)(date, MADRID);
    return {
        dayOfMonth: inMadrid.getDate(),
        month: inMadrid.getMonth() + 1,
        year: inMadrid.getFullYear(),
    };
};
exports.getMadridDateParts = getMadridDateParts;
/**
 * Maps a weekday (DAYS_OF_WEEK: 0=Sun..6=Sat) to the UTC instant of its
 * Madrid-local midnight WITHIN the Wetaca week (Thursday 08:15 → next
 * Thursday 08:15) that contains `weekDate`.
 *
 * ⚠️ Despite the name, this is NOT "the day an order is delivered". It is "day
 * X of the week containing this date", and its non-delivery consumer is the
 * admin's week picker, which draws a row's Thursday→Wednesday span. Shifting
 * it would make that label lie.
 *
 * For an actual delivery date use `deliveryDateForOrderWeek`, which is the same
 * mapping applied to the FOLLOWING week — see there for why.
 */
const deliveryDateForWeekday = (weekDate, dayOfWeek) => {
    const startInMadrid = (0, date_fns_tz_1.toZonedTime)((0, exports.currentStartOfWeek)(weekDate), MADRID); // Thursday 08:15 Madrid
    const offsetDays = (dayOfWeek - exports.WEEK_SCHEDULE.day + 7) % 7;
    const delivery = new Date(startInMadrid);
    delivery.setDate(startInMadrid.getDate() + offsetDays);
    // Reuse the one Madrid-midnight implementation instead of restating it
    // (setHours + fromZonedTime) here — one definition, one behavior.
    return (0, exports.madridStartOfDay)((0, date_fns_tz_1.fromZonedTime)(delivery, MADRID));
};
exports.deliveryDateForWeekday = deliveryDateForWeekday;
/**
 * `HH:MM` (24h) as a time of day, the only shape delivery windows are written in.
 * The hour may be single-digit, since the field it comes from is free text.
 */
const TIME_OF_DAY = /^([01]?\d|2[0-3]):([0-5]\d)$/;
/**
 * The UTC instant at which a `HH:MM` Madrid wall-clock time happens, on the
 * Madrid calendar day of `date`.
 *
 * Both halves of that sentence go wrong the same way if the runtime's zone is
 * used instead. The DAY is read in Madrid, because a delivery date is stored as
 * Madrid midnight and from UTC that instant belongs to the previous calendar
 * day. The OFFSET is the one in force at that time on that day, which on the
 * two Sundays a year the clocks move is not the one in force at midnight.
 *
 * A time it cannot read yields an INVALID Date rather than a guess: the
 * delivery options these come from take `start`/`end` as free text at their
 * write frontier, and a silent midnight would be a delivery window whoever
 * receives it has no way to tell from a real one.
 */
const madridInstantAt = (date, timeOfDay) => {
    const parts = TIME_OF_DAY.exec(timeOfDay);
    if (parts === null)
        return new Date(NaN);
    // The wall clock is composed as TEXT and interpreted in Madrid, never
    // through a Date's local setters: `setHours` resolves against the RUNTIME's
    // zone, and on a day that zone skips the target hour it normalises the time
    // an hour forward — measured under America/New_York on its spring-forward
    // day, where 02:30 Madrid came back an hour late.
    const madridDay = (0, date_fns_tz_1.formatInTimeZone)(date, MADRID, 'yyyy-MM-dd');
    return (0, date_fns_tz_1.fromZonedTime)(`${madridDay}T${parts[1].padStart(2, '0')}:${parts[2]}:00`, MADRID);
};
exports.madridInstantAt = madridInstantAt;
/**
 * The calendar date on which an order placed in the Wetaca week containing
 * `orderWeekDate` gets DELIVERED on the given weekday.
 *
 * A week's label names the MENU week an order belongs to — editing closes on
 * the eve of that week's opening Thursday — and what is ordered for week N is
 * delivered in N+1; this is the one function that applies that shift, and the
 * only place it may live. Resolving delivery on the order's own week would land
 * deliveries during the cooking week itself, before anything has been produced.
 *
 * It steps weeks through `currentEndOfWeek`, which names exactly what it
 * returns (the Thursday 08:15 Madrid that opens the next week) and is itself
 * DST-explicit. ⚠️ A naive `+168h` happens to give the same answer TODAY, and
 * that was measured, not assumed — `deliveryDateForWeekday` re-normalises
 * whatever instant it gets through `currentStartOfWeek`, so an hour of DST
 * drift is absorbed. The reason not to write it that way is that it would rely
 * on that normalisation from a caller that cannot see it.
 *
 * ⚠️ And it is NOT `deliveryDateForWeekday` with an offset baked in. That
 * function means "the day X of the week containing this date" and has a
 * consumer that is not a delivery at all — the admin's week picker, which uses
 * it to draw the Thursday→Wednesday span of a row. Shifting it would make the
 * picker's label lie.
 */
const deliveryDateForOrderWeek = (orderWeekDate, dayOfWeek) => (0, exports.deliveryDateForWeekday)((0, exports.currentEndOfWeek)(orderWeekDate), dayOfWeek);
exports.deliveryDateForOrderWeek = deliveryDateForOrderWeek;
/**
 * Inverse of getWetacaWeekAndYear: the Thursday-08:15-Madrid instant that STARTS
 * the Wetaca week labeled (isoYear, isoWeek) — the label being the ISO week of
 * the CLOSING Thursday. Pure ISO-calendar arithmetic on UTC probes plus the
 * same Madrid-explicit conversion as currentStartOfWeek, so the result never
 * depends on the process TZ.
 */
const startOfWetacaWeek = (isoYear, isoWeek) => {
    // Jan 4th always sits inside ISO week 1; walk back to that week's Monday,
    // then forward to the requested week's Thursday — calendar-only, on UTC probes.
    // 🔴 DO NOT CHANGE THIS DATE 🔴 — ISO 8601: week 1 is the week CONTAINING JANUARY 4th.
    // Jan 1 gives the SAME Monday in 2025 and 2026 and a DIFFERENT one in 7 of the next
    // 16 years — every year whose Jan 1 falls on Fri/Sat/Sun (2021, 2022, 2023, 2027,
    // 2028, 2033, 2034). Every golden here used to live in 2025-2026, so that mutation
    // kept the WHOLE suite green; the 2027 case in test/week.spec.ts is what now catches it.
    const jan4 = new Date(Date.UTC(isoYear, 0, 4));
    const mondayOfWeek1 = new Date(jan4);
    mondayOfWeek1.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7));
    const closingThursday = new Date(mondayOfWeek1);
    closingThursday.setUTCDate(mondayOfWeek1.getUTCDate() + (isoWeek - 1) * 7 + 3);
    // At 00:00Z the closing Thursday is still BEFORE the 08:15-Madrid boundary,
    // so it belongs to the very week it closes; that week starts a Thursday back.
    return (0, exports.currentStartOfWeek)(closingThursday);
};
exports.startOfWetacaWeek = startOfWetacaWeek;
/**
 * Whether (isoYear, isoWeek) names a week that actually exists, by round-tripping
 * the label through startOfWetacaWeek and back. Two failures collapse into this
 * one check, and neither is visible any other way:
 * - a year outside Date's range makes Date.UTC overflow to an Invalid Date, whose
 * parts are all NaN — superjson then serializes the result as `null` rather
 * than throwing, so the caller gets a 200 where the type promises a Date;
 * - week 53 of a 52-week ISO year is absorbed by setUTCDate's rollover and
 * silently returns week 1 of the following year.
 * A predicate and not a throw inside startOfWetacaWeek: this leaves each frontier
 * free to decide (the tRPC input schema turns it into a Zod issue, so an
 * impossible label behaves exactly like a `.max(53)` violation) instead of forcing
 * a try/catch on every caller.
 */
const isRealWetacaWeek = (isoYear, isoWeek) => {
    const label = (0, exports.getWetacaWeekAndYear)((0, exports.startOfWetacaWeek)(isoYear, isoWeek));
    return label.year === isoYear && label.week === isoWeek;
};
exports.isRealWetacaWeek = isRealWetacaWeek;
/**
 * Calendar weekday (0=Sun..6=Sat) of a plain (year, month, day) triple.
 * Pure calendar arithmetic via a UTC probe — involves NO timezone at all, so it
 * can never depend on the process TZ (a local `new Date(y, m-1, d).getDay()`
 * does, through the runtime's midnight). The triple is interpreted as date
 * PARTS, the same convention the delivery-exception matcher uses.
 */
const weekdayOfParts = (year, month, day) => {
    return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
};
exports.weekdayOfParts = weekdayOfParts;
/** The general order cutoff: 13:00 Madrid on the Wednesday before the week opens. */
exports.DEFAULT_CUTOFF_RULE = { weekday: 'WED', hour: 13, minute: 0 };
/** Calendar days between the rule's weekday and the opening Thursday. */
const DAYS_BEFORE_OPENING = { TUE: 2, WED: 1, THU: 0 };
/**
 * Whether a rule's instant falls BEFORE the week opens (Thursday 08:15). Only a
 * Thursday rule can fail it; the frontier rejects such a rule, because a
 * cutoff after the opening would let a store edit an order the kitchen is
 * already cooking.
 */
const cutoffPrecedesWeekOpening = (rule) => rule.weekday !== 'THU'
    || rule.hour * 60 + rule.minute < exports.WEEK_SCHEDULE.hours * 60 + exports.WEEK_SCHEDULE.minutes;
exports.cutoffPrecedesWeekOpening = cutoffPrecedesWeekOpening;
/**
 * The order cutoff for a Wetaca week: by default 13:00 Europe/Madrid on the
 * EVE of the week's opening Thursday (always a Wednesday, since dateFrom is
 * aligned to Thursday 08:15). Editing closes BEFORE the menu week starts:
 * cooking fills the week itself and delivery follows in the next one
 * (deliveryDateForOrderWeek). A store's own `rule` moves the day and the time
 * within the same eve. Wall-clock Madrid arithmetic, so a DST switch never
 * shifts it.
 */
const cutoffForWeek = (weekDate, rule = exports.DEFAULT_CUTOFF_RULE) => {
    const startInMadrid = (0, date_fns_tz_1.toZonedTime)((0, exports.currentStartOfWeek)(weekDate), MADRID); // Thu 08:15 Madrid
    startInMadrid.setDate(startInMadrid.getDate() - DAYS_BEFORE_OPENING[rule.weekday]);
    startInMadrid.setHours(rule.hour, rule.minute, 0, 0);
    return (0, date_fns_tz_1.fromZonedTime)(startInMadrid, MADRID);
};
exports.cutoffForWeek = cutoffForWeek;
/**
 * The week the store is ORDERING for at `now`: the first Wetaca week whose
 * cutoff is still ahead. Editing happens BEFORE a menu week starts (the cutoff
 * is on the eve of its opening Thursday), so this is normally the week AFTER
 * the one containing `now`; between the cutoff and the Thursday-08:15 boundary
 * it is two weeks ahead. `<=` mirrors deriveOrderStatus's strict `<`: at the
 * exact cutoff instant that week is no longer orderable.
 *
 * Without a `rule` this is the GENERAL ordering week. Anything that knows the
 * store passes `store.cutoff`: two stores with different rules can be ordering
 * for different weeks at the same instant.
 */
const currentOrderingWeek = (now = new Date(), rule = exports.DEFAULT_CUTOFF_RULE) => {
    let candidateStart = (0, exports.currentEndOfWeek)(now);
    if ((0, exports.cutoffForWeek)(candidateStart, rule).getTime() <= now.getTime()) {
        candidateStart = (0, exports.currentEndOfWeek)(candidateStart);
    }
    return (0, exports.getWetacaWeekAndYear)(candidateStart);
};
exports.currentOrderingWeek = currentOrderingWeek;
//# sourceMappingURL=week.js.map